import { inject, Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

import { ClientConfigService } from '../client-config.service';

/**
 * Integrates with Azure Application Insights for technical observability:
 * exception logging, custom events, performance metrics, and page-view tracking.
 *
 * Separated from TrackingService because Application Insights serves the
 * engineering team (error rates, performance) while Adobe Analytics serves
 * the business team (user funnels, feature adoption).
 *
 * Initializes lazily from the instrumentation key in client-config.json,
 * so Application Insights is only active in environments where the key is
 * configured. All public log methods silently no-op when AppInsights is
 * not initialized, making it safe to call from any context.
 *
 * A telemetry initializer tags all events with the "administration-portal"
 * cloud role so that portal telemetry is distinguishable from other QIAGEN
 * services sharing the same Application Insights resource.
 */
@Injectable({
  providedIn: 'root'
})
export class MonitoringService {
  private readonly clientConfig = inject(ClientConfigService);

  appInsights: ApplicationInsights;

  constructor() {
    // Defer to break a synchronous DI cycle: when this service is instantiated
    // transitively from authInterceptor → AuthService.ctor → inject(MonitoringService),
    // calling instrumentationKey().subscribe() here would re-enter HttpClient
    // before AuthService finishes constructing, causing NG0200. Running on the
    // next microtask lets the outer construction complete first.
    queueMicrotask(() => this.initializeAppInsights());
  }

  private initializeAppInsights(): void {
    this.clientConfig.instrumentationKey().subscribe(key => {
      if (!key) return;
      this.appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: key,
          enableAutoRouteTracking: true
        }
      });
      this.appInsights.loadAppInsights();
      this.appInsights.addTelemetryInitializer(envelope => {
        envelope.tags['ai.cloud.role'] = 'administration-portal';
        envelope.tags['ai.cloud.roleInstance'] = 'desktop';
      });
      this.interceptConsoleErrors();
    });
  }

  /**
   * Intercepts console.error globally so that all logged errors are
   * forwarded to App Insights at severity Warning (level 2). This
   * covers the 15+ console.error call sites across services and
   * components without requiring individual MonitoringService injection.
   */
  private interceptConsoleErrors(): void {
    const originalConsoleError = console.error.bind(console);
    console.error = (...args: unknown[]) => {
      originalConsoleError(...args);
      const message = args
        .map(a => {
          if (a instanceof Error) return a.message;
          if (typeof a === 'object' && a !== null) {
            try {
              return JSON.stringify(a);
            } catch {
              return Object.prototype.toString.call(a);
            }
          }
          return String(a); // NOSONAR S6551 — `a` is narrowed to primitive here (earlier branches handled Error and object)
        })
        .join(' ');
      this.appInsights?.trackException({
        exception: args[0] instanceof Error ? args[0] : new Error(message),
        severityLevel: 2
      });
    };
  }

  setAuthenticatedUser(userId: string, accountId?: string) {
    if (!userId) return;
    this.appInsights?.setAuthenticatedUserContext(userId, accountId, true);
  }

  clearAuthenticatedUser() {
    this.appInsights?.clearAuthenticatedUserContext();
  }

  logException(exception: Error, severityLevel?: number) {
    this.appInsights?.trackException({ exception, severityLevel });
  }
}
